/**
 * UpdateService - Core service for managing OTA updates
 * Supports selective updates for JS bundles, algorithm code, and model checkpoints
 */

import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { IUpdateManifest, IUpdateComponent, IInstalledVersions, UpdateType } from '../models/Update';

const UPDATE_MANIFEST_STORAGE_KEY = '@update_manifest';
const INSTALLED_VERSIONS_STORAGE_KEY = '@installed_versions';
const PENDING_UPDATES_STORAGE_KEY = '@pending_updates';

const OTA_LOG_PREFIX = '[OTA UpdateService]';

export class UpdateService {
  private static instance: UpdateService;

  private constructor() {}

  private normalizeUpdateType(raw: unknown): string {
    return String(raw || '')
      .trim()
      .toLowerCase()
      .replace(/-/g, '_');
  }

  static getInstance(): UpdateService {
    if (!UpdateService.instance) {
      UpdateService.instance = new UpdateService();
    }
    return UpdateService.instance;
  }

  /**
   * Check for available updates from the backend
   */
  async checkForUpdates(): Promise<IUpdateManifest | null> {
    try {
      const platform = this.getCurrentPlatform();
      const installedVersions = await this.getInstalledVersions();

      console.log(`${OTA_LOG_PREFIX} Checking for updates...`);
      console.log(`${OTA_LOG_PREFIX} Platform: ${platform}`);
      console.log(`${OTA_LOG_PREFIX} Installed versions:`, JSON.stringify(installedVersions, null, 2));

      // Use the updates API endpoint
      const { getUpdateManifest } = require('../api/updates');
      console.log(`${OTA_LOG_PREFIX} Calling getUpdateManifest API...`);
      const manifest = await getUpdateManifest(platform, installedVersions.appVersion);

      if (manifest) {
        // Defensive: backend is not strongly typed; normalize update.type to avoid silent mismatches
        // (e.g. trailing whitespace, hyphen vs underscore, casing).
        try {
          const rawUpdates = Array.isArray(manifest.updates) ? manifest.updates : [];
          const normalizedUpdates = rawUpdates.map((u: any) => ({
            ...u,
            type: this.normalizeUpdateType(u?.type),
          }));
          (manifest as any).updates = normalizedUpdates;
        } catch {
          // ignore - keep original manifest
        }

        console.log(`${OTA_LOG_PREFIX} Manifest received:`, JSON.stringify(manifest, null, 2));
        console.log(`${OTA_LOG_PREFIX} Manifest contains ${manifest.updates?.length || 0} updates`);
        await AsyncStorage.setItem(UPDATE_MANIFEST_STORAGE_KEY, JSON.stringify(manifest));
        console.log(`${OTA_LOG_PREFIX} Manifest saved to AsyncStorage`);
        return manifest;
      }

      console.log(`${OTA_LOG_PREFIX} No manifest returned from API`);
      return null;
    } catch (error) {
      console.error(`${OTA_LOG_PREFIX} Error checking for updates:`, error);
      return null;
    }
  }

  /**
   * Get installed versions of all components
   */
  async getInstalledVersions(): Promise<IInstalledVersions> {
    console.log(`${OTA_LOG_PREFIX} Getting installed versions...`);

    // Get bundled versions - these are the minimum versions we should use
    const bundledVersions = await this.getBundledComponentVersions();
    console.log(`${OTA_LOG_PREFIX} Bundled versions (minimums):`, bundledVersions);

    try {
      const stored = await AsyncStorage.getItem(INSTALLED_VERSIONS_STORAGE_KEY);
      if (stored) {
        const versions = JSON.parse(stored);

        // Ensure we always have the latest app version from the system
        const systemAppVersion = await this.getSystemAppVersion();
        if (systemAppVersion && systemAppVersion !== versions.appVersion) {
          console.log(`${OTA_LOG_PREFIX} Updating appVersion from ${versions.appVersion} to ${systemAppVersion}`);
          versions.appVersion = systemAppVersion;
        }

        // Use bundled version if stored version is missing or lower (prevents downgrade after app update)
        if (!versions.algoCodeVersion || this.compareVersions(bundledVersions.algoCodeVersion, versions.algoCodeVersion) > 0) {
          console.log(`${OTA_LOG_PREFIX} Using bundled algoCodeVersion: ${bundledVersions.algoCodeVersion} (stored: ${versions.algoCodeVersion || 'none'})`);
          versions.algoCodeVersion = bundledVersions.algoCodeVersion;
        }
        if (!versions.checkpointVersion || this.compareVersions(bundledVersions.checkpointVersion, versions.checkpointVersion) > 0) {
          console.log(`${OTA_LOG_PREFIX} Using bundled checkpointVersion: ${bundledVersions.checkpointVersion} (stored: ${versions.checkpointVersion || 'none'})`);
          versions.checkpointVersion = bundledVersions.checkpointVersion;
        }
        // ML runtime packs are installed into AppData (not bundled in the base installer),
        // so do not treat bundled minimums as "installed". Instead, best-effort sync with disk.
        await this.syncMlRuntimeVersionsFromDisk(versions);

        console.log(`${OTA_LOG_PREFIX} Retrieved versions:`, JSON.stringify(versions, null, 2));
        return versions;
      }
      console.log(`${OTA_LOG_PREFIX} No stored versions found, using bundled versions`);
    } catch (error) {
      console.error(`${OTA_LOG_PREFIX} Error reading installed versions:`, error);
    }

    // Get app version from system (Electron) or package.json fallback
    const appVersion = await this.getSystemAppVersion();
    const defaultVersions: IInstalledVersions = {
      appVersion: appVersion,
      algoCodeVersion: bundledVersions.algoCodeVersion,
      checkpointVersion: bundledVersions.checkpointVersion,
    };
    await this.syncMlRuntimeVersionsFromDisk(defaultVersions);
    console.log(`${OTA_LOG_PREFIX} Using default versions:`, JSON.stringify(defaultVersions, null, 2));
    return defaultVersions;
  }

  /**
   * Get the system app version (from VERSION file in Electron, or package.json fallback)
   */
  private async getSystemAppVersion(): Promise<string> {
    console.log(`${OTA_LOG_PREFIX} Getting system app version...`);

    // Check if running in Electron
    const electronAPI = typeof window !== 'undefined' && (window as any).electronAPI;
    if (electronAPI && typeof electronAPI.getAppVersion === 'function') {
      try {
        // Get release stage from environment
        const releaseStage = typeof process !== 'undefined' && process.env?.RELEASE_STAGE
          ? process.env.RELEASE_STAGE
          : 'staging'; // Default to staging if not set

        console.log(`${OTA_LOG_PREFIX} Calling Electron getAppVersion with releaseStage: ${releaseStage}`);
        const version = await electronAPI.getAppVersion(releaseStage);
        console.log(`${OTA_LOG_PREFIX} Electron app version: ${version}`);
        return version;
      } catch (error) {
        console.error(`${OTA_LOG_PREFIX} Error getting Electron app version:`, error);
      }
    }

    // Fallback to package.json
    try {
      const packageJson = require('../../package.json');
      console.log(`${OTA_LOG_PREFIX} Using package.json version: ${packageJson.version}`);
      return packageJson.version || '0.0.0';
    } catch (error) {
      console.error(`${OTA_LOG_PREFIX} Error reading package.json:`, error);
      return '0.0.0';
    }
  }

  /**
   * Get bundled component versions from grams-algo-desktop
   * These are used as minimum versions to prevent downgrading
   */
  private async getBundledComponentVersions(): Promise<{
    algoCodeVersion: string;
    checkpointVersion: string;
    mlRuntimeCpuVersion: string;
    mlRuntimeCudaVersion: string;
  }> {
    console.log(`${OTA_LOG_PREFIX} Getting bundled component versions...`);

    const electronAPI = typeof window !== 'undefined' && (window as any).electronAPI;
    if (electronAPI && typeof electronAPI.getBundledVersions === 'function') {
      try {
        const versions = await electronAPI.getBundledVersions();
        console.log(`${OTA_LOG_PREFIX} Bundled versions:`, versions);
        return versions;
      } catch (error) {
        console.error(`${OTA_LOG_PREFIX} Error getting bundled versions:`, error);
      }
    }

    return {
      algoCodeVersion: '0.0.0',
      checkpointVersion: '0.0.0',
      mlRuntimeCpuVersion: '0.0.0',
      mlRuntimeCudaVersion: '0.0.0',
    };
  }

  private async syncMlRuntimeVersionsFromDisk(versions: IInstalledVersions): Promise<void> {
    const electronAPI = typeof window !== 'undefined' && (window as any).electronAPI;
    if (!electronAPI || typeof electronAPI.getMlRuntimeStatus !== 'function') return;

    try {
      const status = await electronAPI.getMlRuntimeStatus();
      if (!status?.success) return;

      if (status.cpuInstalled) {
        if (status.cpuVersion) {
          if (!versions.mlRuntimeCpuVersion || this.compareVersions(status.cpuVersion, versions.mlRuntimeCpuVersion) > 0) {
            versions.mlRuntimeCpuVersion = status.cpuVersion;
          }
        }
      } else {
        delete (versions as any).mlRuntimeCpuVersion;
      }

      if (status.cudaInstalled) {
        if (status.cudaVersion) {
          if (!versions.mlRuntimeCudaVersion || this.compareVersions(status.cudaVersion, versions.mlRuntimeCudaVersion) > 0) {
            versions.mlRuntimeCudaVersion = status.cudaVersion;
          }
        }
      } else {
        delete (versions as any).mlRuntimeCudaVersion;
      }
    } catch {
      // ignore
    }
  }

  /**
   * Update installed version for a specific component
   */
  async updateInstalledVersion(type: UpdateType, version: string): Promise<void> {
    console.log(`${OTA_LOG_PREFIX} Updating installed version for ${type} to ${version}`);
    try {
      const versions = await this.getInstalledVersions();
      const getKey = (t: UpdateType): keyof IInstalledVersions => {
        switch (t) {
          case 'js_bundle':
            return 'jsBundleVersion';
          case 'algo_code':
            return 'algoCodeVersion';
          case 'checkpoint':
            return 'checkpointVersion';
          case 'ml_runtime_cpu':
            return 'mlRuntimeCpuVersion';
          case 'ml_runtime_cuda':
            return 'mlRuntimeCudaVersion';
          case 'full':
          default:
            return 'appVersion';
        }
      };
      const versionKey = getKey(type);
      const previousVersion = versions[versionKey];
      console.log(`${OTA_LOG_PREFIX} Previous ${type} version: ${previousVersion || 'none'}`);

      switch (type) {
        case 'js_bundle':
          versions.jsBundleVersion = version;
          break;
        case 'algo_code':
          versions.algoCodeVersion = version;
          break;
        case 'checkpoint':
          versions.checkpointVersion = version;
          break;
        case 'ml_runtime_cpu':
          versions.mlRuntimeCpuVersion = version;
          break;
        case 'ml_runtime_cuda':
          versions.mlRuntimeCudaVersion = version;
          break;
      }

      await AsyncStorage.setItem(INSTALLED_VERSIONS_STORAGE_KEY, JSON.stringify(versions));
      console.log(`${OTA_LOG_PREFIX} Successfully updated ${type} version to ${version}`);
    } catch (error) {
      console.error(`${OTA_LOG_PREFIX} Error updating installed version:`, error);
    }
  }

  /**
   * Filter updates applicable to current platform and versions
   */
  async filterApplicableUpdates(manifest: IUpdateManifest): Promise<IUpdateComponent[]> {
    const platform = this.getCurrentPlatform();
    const installedVersions = await this.getInstalledVersions();

    console.log(`${OTA_LOG_PREFIX} Filtering applicable updates...`);
    console.log(`${OTA_LOG_PREFIX} Current platform: ${platform}`);
    console.log(`${OTA_LOG_PREFIX} Total updates in manifest: ${manifest.updates?.length || 0}`);

    const applicableUpdates = manifest.updates
      .map((update) => {
        const normalizedType = this.normalizeUpdateType((update as any)?.type);
        return {
          ...update,
          type: normalizedType as any,
          __rawType: (update as any)?.type,
        } as any;
      })
      .filter(update => {
        const rawType = (update as any).__rawType;
        const normalizedType = String(update.type || '');

        console.log(
          `${OTA_LOG_PREFIX} Evaluating update: type=${normalizedType}, version=${update.version}, platform=${update.platform || 'all'}, rawType=${JSON.stringify(rawType)}`
        );

      // Check platform compatibility
      if (update.platform && update.platform !== 'all' && update.platform !== platform) {
        console.log(`${OTA_LOG_PREFIX}   -> Skipped: platform mismatch (need ${update.platform}, have ${platform})`);
        return false;
      }

      // Check if update is needed based on installed version
      let isApplicable = false;
      let installedVersion: string | undefined;

      switch (normalizedType) {
        case 'js_bundle':
          installedVersion = installedVersions.jsBundleVersion;
          isApplicable = !installedVersion || this.compareVersions(update.version, installedVersion) > 0;
          break;
        case 'algo_code':
          installedVersion = installedVersions.algoCodeVersion;
          isApplicable = !installedVersion || this.compareVersions(update.version, installedVersion) > 0;
          break;
        case 'checkpoint':
          installedVersion = installedVersions.checkpointVersion;
          isApplicable = !installedVersion || this.compareVersions(update.version, installedVersion) > 0;
          break;
        case 'ml_runtime_cpu':
          // CPU runtime is shipped with the base Windows app; do not surface as a user-facing OTA update.
          console.log(`${OTA_LOG_PREFIX}   -> Skipped: ml_runtime_cpu is not user-facing (bundled with app)`);
          return false;
        case 'ml_runtime_cuda':
          installedVersion = installedVersions.mlRuntimeCudaVersion;
          isApplicable = !installedVersion || this.compareVersions(update.version, installedVersion) > 0;
          break;
        case 'full':
          isApplicable = true;
          break;
        default:
          console.log(
            `${OTA_LOG_PREFIX}   -> Skipped: unknown update type`,
            JSON.stringify({
              rawType,
              normalizedType,
              rawTypeLength: typeof rawType === 'string' ? rawType.length : null,
            })
          );
          return false;
      }

      if (isApplicable) {
        console.log(`${OTA_LOG_PREFIX}   -> Applicable: installed=${installedVersion || 'none'}, available=${update.version}`);
      } else {
        console.log(`${OTA_LOG_PREFIX}   -> Skipped: already up-to-date (installed=${installedVersion}, available=${update.version})`);
      }

      return isApplicable;
      })
      .map((update: any) => {
        delete update.__rawType;
        return update as IUpdateComponent;
      });

    console.log(`${OTA_LOG_PREFIX} Applicable updates: ${applicableUpdates.length}`);
    applicableUpdates.forEach(u => {
      console.log(`${OTA_LOG_PREFIX}   - ${u.type} v${u.version} (${u.mandatory ? 'mandatory' : 'optional'}, ${u.size} bytes)`);
    });

    return applicableUpdates;
  }

  /**
   * Store pending updates to be applied on next restart
   */
  async storePendingUpdate(update: IUpdateComponent, downloadedPath: string): Promise<void> {
    console.log(`${OTA_LOG_PREFIX} Storing pending update: type=${update.type}, version=${update.version}, path=${downloadedPath}`);
    try {
      const stored = await AsyncStorage.getItem(PENDING_UPDATES_STORAGE_KEY);
      const pendingUpdatesRaw = stored ? JSON.parse(stored) : [];
      const pendingUpdates = Array.isArray(pendingUpdatesRaw) ? pendingUpdatesRaw : [];

      // De-dupe: keep a single pending entry per (type, version).
      const normalizedType = this.normalizeUpdateType((update as any)?.type);
      const normalizedVersion = String(update.version || '').trim();
      const filtered = pendingUpdates.filter((p: any) => {
        const t = this.normalizeUpdateType(p?.update?.type);
        const v = String(p?.update?.version || '').trim();
        return !(t === normalizedType && v === normalizedVersion);
      });

      filtered.push({
        update,
        downloadedPath,
        timestamp: Date.now(),
      });

      await AsyncStorage.setItem(PENDING_UPDATES_STORAGE_KEY, JSON.stringify(filtered));
      console.log(`${OTA_LOG_PREFIX} Pending update stored successfully. Total pending: ${filtered.length}`);
    } catch (error) {
      console.error(`${OTA_LOG_PREFIX} Error storing pending update:`, error);
    }
  }

  /**
   * Get pending updates waiting to be applied
   */
  async getPendingUpdates(): Promise<Array<{ update: IUpdateComponent; downloadedPath: string; timestamp: number }>> {
    console.log(`${OTA_LOG_PREFIX} Getting pending updates...`);
    try {
      const stored = await AsyncStorage.getItem(PENDING_UPDATES_STORAGE_KEY);
      const pendingUpdates = stored ? JSON.parse(stored) : [];
      console.log(`${OTA_LOG_PREFIX} Found ${pendingUpdates.length} pending updates`);
      pendingUpdates.forEach((p: any) => {
        console.log(`${OTA_LOG_PREFIX}   - ${p.update?.type} v${p.update?.version} at ${p.downloadedPath}`);
      });
      return pendingUpdates;
    } catch (error) {
      console.error(`${OTA_LOG_PREFIX} Error reading pending updates:`, error);
      return [];
    }
  }

  /**
   * Clear pending updates after successful application
   */
  async clearPendingUpdates(): Promise<void> {
    console.log(`${OTA_LOG_PREFIX} Clearing pending updates...`);
    try {
      await AsyncStorage.removeItem(PENDING_UPDATES_STORAGE_KEY);
      console.log(`${OTA_LOG_PREFIX} Pending updates cleared successfully`);
    } catch (error) {
      console.error(`${OTA_LOG_PREFIX} Error clearing pending updates:`, error);
    }
  }

  /**
   * Get current platform identifier
   */
  private getCurrentPlatform(): 'ios' | 'android' | 'web' | 'electron' {
    let platform: 'ios' | 'android' | 'web' | 'electron';
    if (Platform.OS === 'web') {
      // Check if running in Electron
      if (typeof window !== 'undefined' && (window as any).electronAPI) {
        platform = 'electron';
      } else {
        platform = 'web';
      }
    } else {
      platform = Platform.OS as 'ios' | 'android';
    }
    console.log(`${OTA_LOG_PREFIX} Detected platform: ${platform}`);
    return platform;
  }

  /**
   * Compare semantic versions
   * Returns: 1 if v1 > v2, -1 if v1 < v2, 0 if equal
   */
  private compareVersions(v1: string, v2: string): number {
    const parts1 = v1.split('.').map(Number);
    const parts2 = v2.split('.').map(Number);
    
    for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
      const num1 = parts1[i] || 0;
      const num2 = parts2[i] || 0;
      
      if (num1 > num2) return 1;
      if (num1 < num2) return -1;
    }
    
    return 0;
  }
}

export default UpdateService.getInstance();
